import type { ReactNode } from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface UserBubbleProps {
  children: ReactNode;
  className?: string;
  variant?: "default" | "secondary";
}

const UserBubble = ({
  children,
  className,
  variant = "default",
}: UserBubbleProps) => {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [isMultiline, setIsMultiline] = useState(false);

  const syncShape = useCallback(() => {
    const el = bubbleRef.current;
    if (!el) return;
    const cs = getComputedStyle(el);
    let lineHeight = Number.parseFloat(cs.lineHeight);
    if (Number.isNaN(lineHeight) || lineHeight === 0) {
      const fontSize = Number.parseFloat(cs.fontSize);
      lineHeight = Number.isNaN(fontSize) ? 16 : fontSize * 1.25;
    }
    const padY =
      Number.parseFloat(cs.paddingTop) + Number.parseFloat(cs.paddingBottom);
    const contentHeight = el.scrollHeight - padY;
    const approximateLines = Math.round(contentHeight / lineHeight);
    setIsMultiline(approximateLines > 1);
  }, []);

  useLayoutEffect(() => {
    if (variant !== "default") return;

    syncShape();
    const el = bubbleRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const ro = new ResizeObserver(() => {
      syncShape();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [syncShape, children, variant]);

  return (
    <div className={cn("flex w-full min-w-0 justify-end", className)}>
      <div
        ref={variant === "default" ? bubbleRef : undefined}
        className={cn(
          "min-w-0 wrap-break-word whitespace-pre-wrap",
          variant === "secondary"
            ? "max-w-[86%] rounded-[10px] rounded-br-[4px] bg-gray-100 px-3 py-2 text-[13px] leading-relaxed text-gray-800"
            : cn(
                "max-w-full bg-gray-100 px-6 py-2.5 text-sm leading-relaxed text-gray-800",
                isMultiline ? "rounded-[20px]" : "rounded-full",
              ),
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default UserBubble;
